import type { TourLayout, TourRect } from './types';

export const tourLayoutOf = (scenarioId: string): TourLayout | null => {
  if (scenarioId.endsWith('-mobile')) {
    return 'mobile';
  }

  if (scenarioId.endsWith('-desktop')) {
    return 'desktop';
  }

  return null;
};

export const findStepIndex = (
  steps: readonly { targetId: string }[],
  startIndex: number,
  direction: 1 | -1,
  isVisible: (targetId: string) => boolean,
): number | null => {
  const lastIndex = steps.length - 1;
  let index = startIndex;

  while (index >= 0 && index <= lastIndex) {
    if (isVisible(steps[index].targetId)) {
      return index;
    }

    index += direction;
  }

  return null;
};

export const isTourTargetVisible = (element: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  const style = window.getComputedStyle(element);

  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 8 && rect.height > 8;
};

export const expandTourRect = (
  rect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom'>,
  padding: number,
  viewport: { width: number; height: number },
): TourRect => {
  // Always keep the hole inside the viewport — an off-screen target must not
  // produce top > bottom (zero-height hole + misplaced tooltip).
  const top = Math.min(viewport.height, Math.max(0, rect.top - padding));
  const left = Math.min(viewport.width, Math.max(0, rect.left - padding));
  const right = Math.min(viewport.width, Math.max(left, rect.right + padding));
  const bottom = Math.min(viewport.height, Math.max(top, rect.bottom + padding));

  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
    bottom,
    right,
  };
};

/** True when the target intersects the viewport enough to spotlight. */
export const isTourTargetInViewport = (
  rect: Pick<DOMRect, 'top' | 'bottom' | 'width' | 'height'>,
  viewport: { height: number },
  _padding = 0,
): boolean => {
  if (rect.width <= 8 || rect.height <= 8) {
    return false;
  }

  const visibleTop = Math.max(0, rect.top);
  const visibleBottom = Math.min(viewport.height, rect.bottom);
  const visibleHeight = visibleBottom - visibleTop;

  if (visibleHeight <= 8) {
    return false;
  }

  // Small targets (e.g. menu icon): require most of the element.
  // Tall targets: require a meaningful slice, not the full height.
  const required = Math.min(rect.height * 0.8, 48);

  return visibleHeight >= required;
};

const TOOLTIP_GAP = 12;
const TOOLTIP_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_ESTIMATED_HEIGHT = 200;

export const placeTourTooltip = (
  hole: Pick<TourRect, 'top' | 'bottom' | 'left'>,
  viewport: { width: number; height: number },
): { top: number; left: number; width: number } => {
  const width = Math.min(TOOLTIP_MAX_WIDTH, Math.max(0, viewport.width - TOOLTIP_MARGIN * 2));
  const left = Math.min(Math.max(TOOLTIP_MARGIN, hole.left), Math.max(TOOLTIP_MARGIN, viewport.width - width - TOOLTIP_MARGIN));
  const belowTop = hole.bottom + TOOLTIP_GAP;
  const fitsBelow = belowTop + TOOLTIP_ESTIMATED_HEIGHT <= viewport.height - TOOLTIP_MARGIN;
  const aboveTop = hole.top - TOOLTIP_GAP - TOOLTIP_ESTIMATED_HEIGHT;
  const unclampedTop = fitsBelow ? belowTop : aboveTop;
  const top = Math.min(
    Math.max(TOOLTIP_MARGIN, unclampedTop),
    Math.max(TOOLTIP_MARGIN, viewport.height - TOOLTIP_ESTIMATED_HEIGHT - TOOLTIP_MARGIN),
  );

  return { top, left, width };
};
